/**
 * Operational Profile Context — Task #2324
 *
 * Provides the tenant's operational profile to the entire application.
 * Drives:
 *   - Profile-adaptive navigation overlay in AppLayout
 *   - Default login-landing-route
 *   - Per-module fallback behaviours
 */

import {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./auth-context";

// ─── Types ─────────────────────────────────────────────────────────────────

export type ProfileType =
  | "full_platform"
  | "ptw_only"
  | "safety_first"
  | "workforce_management"
  | "time_attendance_only"
  | "credentials_only"
  | "access_control_only"
  | "custom";

export interface FallbackBehavior {
  mode: "hidden" | "placeholder" | "standalone";
  message?: string;
}

export interface ChecklistStep {
  id: string;
  label: string;
  description: string;
  href?: string;
  required: boolean;
}

export interface ProfileChecklist {
  key: string;
  label: string;
  steps: ChecklistStep[];
}

export interface OperationalProfile {
  tenantId: string;
  profileType: ProfileType;
  label: string;
  description: string;
  enabledFeatureFlags: string[];
  visibleNavGroups: string[];
  defaultLandingRoute: string;
  fallbackBehaviors: Record<string, FallbackBehavior>;
  partnerCapabilityDefaults: string[];
  featureOverrides: Record<string, boolean> | null;
  checklist: ProfileChecklist | null;
  updatedAt: string | null;
}

interface OperationalProfileContextValue {
  profile: OperationalProfile | null;
  isLoading: boolean;
  /** true when visibleNavGroups is non-empty (i.e. not full_platform / custom) */
  hasNavOverlay: boolean;
  /** Returns true when this nav group ID is visible in the current profile overlay */
  isNavGroupVisible: (groupId: string) => boolean;
  /**
   * Returns true when the given feature flag key is enabled for this tenant.
   * Always returns true for Super Admins (no profile restriction applies).
   */
  isModuleEnabled: (featureKey: string) => boolean;
  /**
   * Returns the fallback behaviour for a given feature flag key, or null when
   * the module is enabled / no fallback is configured.
   */
  getFallbackBehavior: (featureKey: string) => FallbackBehavior | null;
  /** @deprecated use getFallbackBehavior */
  getFallback: (featureKey: string) => FallbackBehavior | null;
}

// ─── Context ────────────────────────────────────────────────────────────────

const OperationalProfileContext =
  createContext<OperationalProfileContextValue>({
    profile: null,
    isLoading: false,
    hasNavOverlay: false,
    isNavGroupVisible: () => true,
    isModuleEnabled: () => true,
    getFallbackBehavior: () => null,
    getFallback: () => null,
  });

// ─── Provider ────────────────────────────────────────────────────────────────

export function OperationalProfileProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { currentUser, isAuthenticated } = useAuth();

  const isSuperAdmin = currentUser?.role === "Super Admin";

  const { data, isLoading } = useQuery<{ data: OperationalProfile }>({
    queryKey: ["/api/admin/operational-profile"],
    enabled: isAuthenticated && !!currentUser && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const profile = data?.data ?? null;

  const hasNavOverlay =
    !!profile &&
    profile.visibleNavGroups.length > 0 &&
    profile.profileType !== "full_platform" &&
    profile.profileType !== "custom";

  const visibleNavGroupSet = new Set(profile?.visibleNavGroups ?? []);
  const enabledFlagSet = new Set(profile?.enabledFeatureFlags ?? []);

  function isNavGroupVisible(groupId: string): boolean {
    if (isSuperAdmin) return true;
    if (!hasNavOverlay) return true;
    return visibleNavGroupSet.has(groupId);
  }

  function isModuleEnabled(featureKey: string): boolean {
    if (isSuperAdmin) return true;
    if (!profile) return true;
    return enabledFlagSet.has(featureKey);
  }

  function getFallbackBehavior(featureKey: string): FallbackBehavior | null {
    if (!profile) return null;
    return profile.fallbackBehaviors[featureKey] ?? null;
  }

  return (
    <OperationalProfileContext.Provider
      value={{
        profile,
        isLoading,
        hasNavOverlay,
        isNavGroupVisible,
        isModuleEnabled,
        getFallbackBehavior,
        getFallback: getFallbackBehavior,
      }}
    >
      {children}
    </OperationalProfileContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOperationalProfile(): OperationalProfileContextValue {
  return useContext(OperationalProfileContext);
}
